import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/Alert";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { SUPABASE } from "@/lib/db";
import { endpoints } from "@/api/endpoints";
import { authenticatedRequestBody } from "@/api/services/auth";
import GoogleIcon from "./img/google-icon.svg";
import MicrosoftIcon from "./img/microsoft-icon.svg";
import { CenteredCard } from "@/components/CenteredCard";
import { signInWithGoogle, signInWithMicrosoft } from "@/lib/auth";
import { OAuthButton } from "@/components/OAuthButton";

type LoginResponse = {
  error: string;
};

function getRedirectTo(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("redirect_to");
}

function isAllowedRedirect(url: string) {
  return (
    url.startsWith("https://estimate.nestrr.net") ||
    url.startsWith("https://www.nestrr.net") ||
    url.startsWith("http://localhost:3000")
  );
}

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<LoginResponse | null>(null);

  const navigate = useNavigate();

  const redirectTo = useMemo(() => {
    const raw = getRedirectTo();
    if (!raw) return null;
    if (!isAllowedRedirect(raw)) return null;
    return raw;
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(e.target.value);
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPassword(e.target.value);

  const goAfterLogin = () => {
    if (redirectTo) {
      window.location.assign(redirectTo);
      return;
    }
    navigate("/app");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { data, error } = await SUPABASE.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError({ error: error.message });
      return;
    }

    if (data?.session && redirectTo) {
      const isCrossDomain = !redirectTo.startsWith(window.location.origin);

      if (isCrossDomain) {
        try {
          const body = await authenticatedRequestBody(null);
          const resp = await fetch(endpoints.handoff, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
          });

          if (!resp.ok) throw new Error("Handoff request failed");

          const { code } = await resp.json();
          const url = new URL(redirectTo);
          url.searchParams.set("code", code);
          window.location.assign(url.toString());
          return;
        } catch (err) {
          setError({ error: "Failed to redirect. Please try again." });
          return;
        }
      }
    }

    goAfterLogin();
  };

  const oauthRedirectTo = redirectTo ?? `${window.location.origin}/app`;

  return (
    <CenteredCard>
      <CardHeader>
        <CardTitle>Login</CardTitle>
        <CardDescription>
          Please login, or{" "}
          <Link to="/register" className={"text-blue-500 underline"}>
            register
          </Link>
          .
        </CardDescription>
      </CardHeader>

      <CardContent>
        {error && (
          <Alert variant={"destructive"} className={"mb-4"}>
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
            <AlertTitle>Uh Oh!</AlertTitle>
            <AlertDescription>{error?.error}</AlertDescription>
          </Alert>
        )}

        <div className={"flex flex-col justify-around mb-4 w-full gap-2"}>
          <OAuthButton
            callback={() => {
              signInWithGoogle(oauthRedirectTo);
            }}
            icon={GoogleIcon}
            text={"Sign in with Google"}
          />
          <OAuthButton
            callback={() => signInWithMicrosoft(oauthRedirectTo)}
            icon={MicrosoftIcon}
            text={"Sign in with Microsoft"}
          />
        </div>

        <form onSubmit={handleSubmit} id={"login_form"}>
          <div>
            <Label>Email</Label>
            <Input
              type="text"
              value={email}
              onChange={handleEmailChange}
              required
            />
          </div>

          <div>
            <Label>Password</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              required
            />
          </div>
        </form>
      </CardContent>

      <CardFooter className={"flex justify-end"}>
        <Link to="/forgot-password" className={"text-blue-500 underline mr-4"}>
          Forgot Password?
        </Link>
        <Button type="submit" form={"login_form"}>
          Login
        </Button>
      </CardFooter>
    </CenteredCard>
  );
};
